(() => {
  class WholesaleDelivery extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-delivery-content]');
      this.button = this.querySelector('[data-delivery-toggle]');
      if (!this.content) return;
      if (!this.button) {
        this.content.style.height = 'auto';
        return;
      }

      this.initialExpanded = this.dataset.startCollapsed !== 'true';
      this.mobileQuery = window.matchMedia('(max-width: 560px)');
      this.alwaysCollapseMobile = this.dataset.alwaysCollapseMobile === 'true';
      this.expanded = this.alwaysCollapseMobile && this.mobileQuery.matches ? false : this.initialExpanded;
      this.animating = false;
      this.buttonHandler = () => this.animateTo(!this.expanded);
      this.resizeHandler = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.refreshHeight());
      };
      this.viewportHandler = event => {
        const expanded = event.matches && this.alwaysCollapseMobile ? false : this.initialExpanded;
        this.setState(expanded);
      };
      this.transitionHandler = event => {
        if (event.propertyName === 'height') this.finishAnimation();
      };

      this.button.addEventListener('click', this.buttonHandler);
      this.content.addEventListener('transitionend', this.transitionHandler);
      window.addEventListener('resize', this.resizeHandler, { passive: true });
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.viewportHandler);
      else this.mobileQuery.addListener(this.viewportHandler);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.animating) this.resizeHandler();
        });
        const inner = this.content.firstElementChild;
        if (inner) this.resizeObserver.observe(inner);
      }

      this.renderState();
      this.content.style.height = this.expanded ? 'auto' : '0px';
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.buttonHandler);
      this.content?.removeEventListener('transitionend', this.transitionHandler);
      window.removeEventListener('resize', this.resizeHandler);
      cancelAnimationFrame(this.resizeFrame);
      clearTimeout(this.animationTimer);
      this.resizeObserver?.disconnect();
      if (!this.mobileQuery) return;
      if (this.mobileQuery.removeEventListener) this.mobileQuery.removeEventListener('change', this.viewportHandler);
      else this.mobileQuery.removeListener(this.viewportHandler);
    }

    setState(expanded) {
      clearTimeout(this.animationTimer);
      this.animating = false;
      this.expanded = expanded;
      this.renderState();
      this.content.style.height = expanded ? 'auto' : '0px';
    }

    animateTo(expanded) {
      clearTimeout(this.animationTimer);
      const startHeight = this.content.getBoundingClientRect().height;
      const targetHeight = expanded ? this.content.scrollHeight : 0;
      this.content.style.height = `${startHeight}px`;
      this.content.getBoundingClientRect();
      this.expanded = expanded;
      this.animating = true;
      this.renderState();
      requestAnimationFrame(() => {
        this.content.style.height = `${targetHeight}px`;
      });
      this.animationTimer = setTimeout(() => this.finishAnimation(), 450);
    }

    finishAnimation() {
      if (!this.animating) return;
      clearTimeout(this.animationTimer);
      this.animating = false;
      this.content.style.height = this.expanded ? 'auto' : '0px';
    }

    refreshHeight() {
      if (this.animating) return;
      this.content.style.height = this.expanded ? 'auto' : '0px';
    }

    renderState() {
      this.classList.toggle('is-expanded', this.expanded);
      this.button.setAttribute('aria-expanded', String(this.expanded));
      this.querySelector('[data-delivery-more]').hidden = this.expanded;
      this.querySelector('[data-delivery-less]').hidden = !this.expanded;
      const arrow = this.querySelector('[data-delivery-arrow]');
      if (arrow) arrow.textContent = this.expanded ? '↑' : '↓';
    }
  }

  class WholesaleDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.rangeElement = this.querySelector('[data-delivery-estimate-range]');
      if (!this.rangeElement) return;
      this.render();
      this.refreshTimer = window.setInterval(() => this.render(), 60000);
    }

    disconnectedCallback() {
      window.clearInterval(this.refreshTimer);
    }

    getLondonCalendar(now) {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.dataset.timeZone || 'Europe/London',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        hourCycle: 'h23'
      });
      const parts = Object.fromEntries(
        formatter.formatToParts(now)
          .filter(part => part.type !== 'literal')
          .map(part => [part.type, Number(part.value)])
      );
      return {
        date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
        hour: parts.hour
      };
    }

    isWorkingDay(date) {
      const day = date.getUTCDay();
      return day !== 0 && day !== 6;
    }

    nextWorkingDay(date) {
      const next = new Date(date);
      do next.setUTCDate(next.getUTCDate() + 1);
      while (!this.isWorkingDay(next));
      return next;
    }

    addWorkingDays(date, count) {
      let result = new Date(date);
      let added = 0;
      while (added < count) {
        result.setUTCDate(result.getUTCDate() + 1);
        if (this.isWorkingDay(result)) added += 1;
      }
      return result;
    }

    calculateRange(now = new Date()) {
      const { date: londonDate, hour } = this.getLondonCalendar(now);
      const cutoffHour = Number(this.dataset.cutoffHour || 14);
      const minDays = Number(this.dataset.minDays || 2);
      const maxDays = Number(this.dataset.maxDays || 5);
      const startDate = !this.isWorkingDay(londonDate) || hour >= cutoffHour
        ? this.nextWorkingDay(londonDate)
        : londonDate;
      return {
        earliest: this.addWorkingDays(startDate, minDays),
        latest: this.addWorkingDays(startDate, maxDays)
      };
    }

    formatRange(earliest, latest) {
      const day = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric' });
      const dayMonth = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'long' });
      const dayMonthYear = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' });
      const sameMonth = earliest.getUTCMonth() === latest.getUTCMonth();
      const sameYear = earliest.getUTCFullYear() === latest.getUTCFullYear();

      if (sameMonth && sameYear) {
        const month = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long' }).format(latest);
        return `${day.format(earliest)}–${day.format(latest)} ${month}`;
      }
      if (sameYear) return `${dayMonth.format(earliest)}–${dayMonth.format(latest)}`;
      return `${dayMonthYear.format(earliest)}–${dayMonthYear.format(latest)}`;
    }

    render(now = new Date()) {
      const range = this.calculateRange(now);
      this.rangeElement.textContent = this.formatRange(range.earliest, range.latest);
    }
  }

  if (!customElements.get('wholesale-delivery')) customElements.define('wholesale-delivery', WholesaleDelivery);
  if (!customElements.get('wholesale-delivery-estimate')) customElements.define('wholesale-delivery-estimate', WholesaleDeliveryEstimate);
})();
